import { collection, doc, CollectionReference, DocumentReference } from 'firebase/firestore';
import { db } from '../config/firebase';

// Top-level user document.
export const userDoc = (uid: string): DocumentReference =>
  doc(db, 'users', uid);

/** Single-doc bag of the user's owned cards. Replaces per-card collection docs. */
export const userCollectionBagDoc = (uid: string): DocumentReference =>
  doc(db, 'users', uid, 'data', 'collection');

// Binders (per user) and pocket placements inside each binder.
export const userBindersCollection = (uid: string): CollectionReference =>
  collection(db, 'users', uid, 'binders');

export const userBinderDoc = (
  uid: string,
  binderId: string
): DocumentReference => doc(db, 'users', uid, 'binders', binderId);

export const binderPlacementsCollection = (
  uid: string,
  binderId: string
): CollectionReference => collection(db, 'users', uid, 'binders', binderId, 'placements');

export const binderPlacementDoc = (
  uid: string,
  binderId: string,
  slotKey: string
): DocumentReference => doc(db, 'users', uid, 'binders', binderId, 'placements', slotKey);

export const binderPlacementCountsCollection = (
  uid: string,
  binderId: string
): CollectionReference => collection(db, 'users', uid, 'binders', binderId, 'placementCounts');

export const binderPlacementCountDoc = (
  uid: string,
  binderId: string,
  cardId: string
): DocumentReference => doc(db, 'users', uid, 'binders', binderId, 'placementCounts', cardId);

// Public directory entry (slug-keyed) for opt-in profile discovery.
export const publicDirectoryDoc = (slug: string): DocumentReference =>
  doc(db, 'publicDirectory', slug.toLowerCase());

/** UID-keyed flag so collection subcollection reads can be authorized without exposing user emails. */
export const publicCollectionDoc = (uid: string): DocumentReference =>
  doc(db, 'publicCollections', uid);

// Saved searches (per user).
export const userSavedSearches = (uid: string): CollectionReference =>
  collection(db, 'users', uid, 'savedSearches');

// Decks (top-level collection for public sharing).
export const decksCollection = (): CollectionReference =>
  collection(db, 'decks');

export const deckDoc = (deckId: string): DocumentReference =>
  doc(db, 'decks', deckId);

export const deckVersionsCollection = (deckId: string): CollectionReference =>
  collection(db, 'decks', deckId, 'versions');

export const deckVersionDoc = (deckId: string, versionNumber: number): DocumentReference =>
  doc(db, 'decks', deckId, 'versions', String(versionNumber));

// Like tracking (one doc per user per deck).
export const deckLikesCollection = (deckId: string): CollectionReference =>
  collection(db, 'deckLikes', deckId, 'likes');

export const deckLikeDoc = (deckId: string, uid: string): DocumentReference =>
  doc(db, 'deckLikes', deckId, 'likes', uid);

// Live match tracking sessions.
export const matchSessionsCollection = (): CollectionReference =>
  collection(db, 'matchSessions');

export const matchSessionDoc = (sessionId: string): DocumentReference =>
  doc(db, 'matchSessions', sessionId);

// Short human-enterable join codes.
export const shortMatchCodeDoc = (shortCode: string): DocumentReference =>
  doc(db, 'shortMatchCodes', shortCode.toUpperCase());

// Canonical match results.
export const matchResultsCollection = (): CollectionReference =>
  collection(db, 'matchResults');

export const matchResultDoc = (matchResultId: string): DocumentReference =>
  doc(db, 'matchResults', matchResultId);

// Per-user private history refs optimized for timeline and stats queries.
export const userMatchResultRefsCollection = (uid: string): CollectionReference =>
  collection(db, 'users', uid, 'matchResultRefs');

export const userMatchResultRefDoc = (uid: string, matchResultId: string): DocumentReference =>
  doc(db, 'users', uid, 'matchResultRefs', matchResultId);

// Private per-user multi-round events (Nexus Night, skirmish, tournament).
export const userMatchEventsCollection = (uid: string): CollectionReference =>
  collection(db, 'users', uid, 'matchEvents');

export const userMatchEventDoc = (uid: string, eventId: string): DocumentReference =>
  doc(db, 'users', uid, 'matchEvents', eventId);

// ─── Teams ───────────────────────────────────────────────────────────────────

export const teamsCollection = (): CollectionReference => collection(db, 'teams');

export const teamDoc = (teamId: string): DocumentReference => doc(db, 'teams', teamId);

export const teamSlugDoc = (slug: string): DocumentReference =>
  doc(db, 'teamSlugs', slug.toLowerCase());

export const publicTeamDirectoryDoc = (slug: string): DocumentReference =>
  doc(db, 'publicTeamDirectory', slug.toLowerCase());

export const publicTeamDirectoryCollection = (): CollectionReference =>
  collection(db, 'publicTeamDirectory');

export const teamInvitesCollection = (teamId: string): CollectionReference =>
  collection(db, 'teams', teamId, 'invites');

export const teamInviteDoc = (teamId: string, inviteId: string): DocumentReference =>
  doc(db, 'teams', teamId, 'invites', inviteId);

export const teamRequestsCollection = (teamId: string): CollectionReference =>
  collection(db, 'teams', teamId, 'requests');

export const teamRequestDoc = (teamId: string, requestId: string): DocumentReference =>
  doc(db, 'teams', teamId, 'requests', requestId);

export const teamSharedCollectionDoc = (teamId: string, uid: string): DocumentReference =>
  doc(db, 'teams', teamId, 'sharedCollections', uid);

export const teamSharedCollectionsCollection = (teamId: string): CollectionReference =>
  collection(db, 'teams', teamId, 'sharedCollections');
